/**
 * FeatureImportConfigDialog — per-simulation feature importer configuration.
 *
 * Built on the app `Dialog` (`@/components/dialog`, platform `<dialog>` —
 * click-outside does not dismiss) and rendered conditionally: the caller keeps
 * it mounted only while open, so field state resets on every open.
 *
 * Fields (accessible names pinned by the acceptance tests):
 *  - "Tracker source" — select over the tracker sources, defaulting to the
 *    stored config's source, else the first source;
 *  - "Features query" — tracker query (e.g. JQL for epics), required;
 *  - "Due date field" — searchable picker over the source's discovered
 *    fields, filtered to date-typed ones by default ("Show all fields"
 *    reveals the rest; sources that report no types at all show everything
 *    automatically). No selection = unmapped, shipcast keeps owning due
 *    dates. When field discovery fails (tracker unreachable) the picker
 *    degrades to the previous free-text input so the config stays editable;
 *  - "Preserve existing order" — checkbox, checked by default.
 *
 * Actions: "OK" (disabled while the query is empty; saves then closes) and
 * "Cancel" (closes without saving).
 */

import { useId, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button, Input } from '@lean-agility/design-system/react';

import { Dialog, DialogActions } from '@/components/dialog';
import { Checkbox } from '@/components/atoms';
import { SearchableSelect, type SearchableSelectOption } from '@/components/molecules';
import { useSources } from '@/features/tracker/hooks';

import { useFeatureImportConfig, useSaveFeatureImportConfig, useSourceFields } from '../hooks';
import type { TrackerField } from '../types';

export interface FeatureImportConfigDialogProps {
  simulationId: string | null;
  open?: boolean;
  onClose: () => void;
}

export function FeatureImportConfigDialog({ simulationId, open = false, onClose }: FeatureImportConfigDialogProps) {
  if (!open) return null;
  return <ConfigDialogContent simulationId={simulationId} onClose={onClose} />;
}

/** The picker's option for a discovered field: value = the id the config
 * stores (falling back to the name when the tracker has no stable id),
 * label = the human name with the id shown subtly when present. */
function fieldOption(f: TrackerField): SearchableSelectOption {
  return {
    value: f.id ?? f.name,
    label: f.id ? `${f.name} (${f.id})` : f.name,
  };
}

function ConfigDialogContent({ simulationId, onClose }: { simulationId: string | null; onClose: () => void }) {
  const sourceSelectId = useId();
  const queryFieldId = useId();
  const dueDateFieldId = useId();

  const { data: sources } = useSources();
  const { data: config } = useFeatureImportConfig(simulationId);
  const save = useSaveFeatureImportConfig(simulationId);

  // Seeded from the stored config at mount; the dialog is conditionally
  // rendered, so every open starts from the saved values.
  const [sourceId, setSourceId] = useState('');
  const [query, setQuery] = useState(() => config?.features_query ?? '');
  const [dueDateField, setDueDateField] = useState(() => config?.due_date_field ?? '');
  const [preserveOrder, setPreserveOrder] = useState(() => config?.preserve_order ?? true);
  const [showAllFields, setShowAllFields] = useState(false);

  // A native select with no placeholder option shows its first option, so the
  // submitted value must match: explicit pick → stored config → first source.
  const effectiveSourceId = sourceId || config?.tracker_source_id || sources?.[0]?.id || '';

  const fieldsQuery = useSourceFields(effectiveSourceId || null);
  const fields = fieldsQuery.data;

  const dateFields = useMemo(
    () => (fields ?? []).filter((f) => f.field_type === 'date' || f.field_type === 'datetime'),
    [fields],
  );
  // A source may report no field types at all — then the date filter would
  // hide every field, so show everything automatically instead of an empty
  // picker with a checkbox nobody would guess at.
  const noDateFields = !!fields && fields.length > 0 && dateFields.length === 0;
  const showAll = showAllFields || noDateFields;

  const fieldOptions = useMemo<SearchableSelectOption[]>(() => {
    const options = (showAll ? (fields ?? []) : dateFields).map(fieldOption);
    // Keep a stored mapping visible even when the filter (or a changed
    // tracker) would hide it — otherwise the button would show the
    // placeholder while silently submitting the stored value.
    if (dueDateField && !options.some((o) => o.value === dueDateField)) {
      const known = (fields ?? []).find((f) => (f.id ?? f.name) === dueDateField);
      options.push(known ? fieldOption(known) : { value: dueDateField, label: dueDateField });
    }
    return options;
  }, [showAll, fields, dateFields, dueDateField]);

  const busy = save.isPending;
  const canSave = query.trim().length > 0 && !busy;

  const onOk = async () => {
    try {
      await save.mutateAsync({
        tracker_source_id: effectiveSourceId,
        features_query: query,
        due_date_field: dueDateField.trim() ? dueDateField : null,
        preserve_order: preserveOrder,
      });
      onClose();
    } catch {
      // mutation state carries the error; surfaced below
    }
  };

  return (
    <Dialog title="Feature import settings" onClose={onClose} dismissible={!busy} maxWidth="max-w-lg">
      <div className="space-y-4">
        <div>
          <label htmlFor={sourceSelectId} className="mb-1 block text-sm font-medium">
            Tracker source
          </label>
          <select
            id={sourceSelectId}
            value={effectiveSourceId}
            onChange={(e) => setSourceId(e.target.value)}
            disabled={busy}
            className="
              w-full rounded-sm border px-3 py-2 text-sm
              disabled:bg-bg-muted
            "
          >
            {sources && sources.length > 0 ? (
              sources.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.kind})
                </option>
              ))
            ) : (
              <option value="">No tracker sources configured</option>
            )}
          </select>
        </div>
        <div>
          <label htmlFor={queryFieldId} className="mb-1 block text-sm font-medium">
            Features query
          </label>
          <textarea
            id={queryFieldId}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            rows={2}
            className="
              w-full rounded-sm border px-3 py-2 font-mono text-sm
              disabled:bg-bg-muted
            "
            placeholder="project = ACME AND type = Epic"
            disabled={busy}
          />
        </div>
        {fieldsQuery.isError ? (
          // Field discovery failed (tracker unreachable, credentials
          // rejected…) — degrade to the previous free-text input so the
          // config stays editable rather than dead-ending the dialog.
          <Input
            label="Due date field"
            value={dueDateField}
            onChange={(e) => setDueDateField(e.target.value)}
            placeholder="duedate"
            disabled={busy}
            hint="Field discovery failed — enter the tracker field id manually. Empty = due dates stay owned by Shipcast."
          />
        ) : (
          <div>
            <label htmlFor={dueDateFieldId} className="mb-1 block text-sm font-medium">
              Due date field
            </label>
            <SearchableSelect
              id={dueDateFieldId}
              value={dueDateField || null}
              onChange={(v) => setDueDateField(v ?? '')}
              options={fieldOptions}
              placeholder={fieldsQuery.isLoading ? 'Loading fields…' : 'No due-date mapping'}
              searchPlaceholder="Search fields..."
              noOptionsMessage={fieldsQuery.isLoading ? 'Loading fields…' : 'No fields discovered'}
              allowClear
              clearLabel="No due-date mapping"
              disabled={busy || fieldsQuery.isLoading}
            />
            <p className="mt-1 text-xs text-text-muted">
              Tracker field carrying the feature due date. Empty = due dates stay owned by Shipcast.
            </p>
            {noDateFields ? (
              <p className="mt-1 text-xs text-text-muted">
                This source reports no date-typed fields — showing all fields.
              </p>
            ) : (
              (fields?.length ?? 0) > dateFields.length && (
                <div className="mt-1">
                  <Checkbox
                    label="Show all fields"
                    checked={showAllFields}
                    onChange={(e) => setShowAllFields(e.target.checked)}
                    disabled={busy}
                  />
                </div>
              )
            )}
          </div>
        )}
        <Checkbox
          label="Preserve existing order"
          checked={preserveOrder}
          onChange={(e) => setPreserveOrder(e.target.checked)}
          disabled={busy}
          hint="Keep current feature positions; imported features append at the end."
        />
        {save.error instanceof Error && <p className="text-sm text-feedback-error">{save.error.message}</p>}
        <DialogActions>
          <Button variant="ghost" type="button" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" type="button" onClick={onOk} disabled={!canSave}>
            {busy ? (
              <>
                <Loader2 className="size-4 animate-spin" /> Saving…
              </>
            ) : (
              'OK'
            )}
          </Button>
        </DialogActions>
      </div>
    </Dialog>
  );
}
